import React from 'react';

import { observer } from 'mobx-react';

import { useParams } from 'react-router-dom';

import Record from 'ui/Record/Record';

import { DefaultFeedMapping } from 'ui/Feed/Feed';

import { CenteredCircularProgress } from 'components/Progress';

import NotFound from 'containers/Error/Pages/NotFound/NotFound';

import DataError from 'containers/Error/DataError';

import { isNotFound } from 'utils/api/errors';

import { useNavBarHeight } from 'utils/hooks/navbar';
import { useStore } from 'utils/hooks/store';

import EntityDetailsContent from 'components/Entities/EntityDetailsContent';

import EntityDetailsHeader from 'components/Entities/EntityDetailsHeader';
import EntityDetailsActivities from 'components/Entities/EntityDetailsActivities';
import EntityDetailsActivitiesFooter from 'components/Entities/EntityDetailsActivities/EntityDetailsActivitiesFooter';

import { ActivityMapping } from 'utils/api/types';
import EntitiesDetailsLoader from 'ui/DetailsLoader/EntitiesDetailsLoader';

import RiskHeaderValue from './components/RiskHeaderValue/RiskHeaderValue';

const activityTypeToComponent = {
  ...DefaultFeedMapping,
} as unknown as ActivityMapping;

// [label, field key, testid suffix]
const HEADER_FIELDS: [string, string, string][] = [
  ['Owner', 'risks__owner', 'owner'],
  ['Status', 'risks__status', 'status'],
  ['Next review date', 'risks__nextReviewDate', 'next-review-date'],
  ['Inherent score', 'risks__initialRisk', 'inherent'],
  ['Residual score', 'risks__residualRisk', 'residual'],
];

const ACTIVITIES_WIDTH = 296;

const RisksDetails: React.FunctionComponent = (): React.ReactElement => {
  const navBarHeight = useNavBarHeight();
  const params = useParams<{ entityId: string }>();

  const { riskDetails, session } = useStore();
  const moduleEnabled = Boolean(session.user?.risksAssessmentsModuleEnabled);

  return (
    <EntitiesDetailsLoader params={params} routeParam="entityId" store={riskDetails}>
      {({ isLoaded, error, reload }): React.ReactElement => {
        if (!isLoaded) {
          return <CenteredCircularProgress />;
        }
        if (riskDetails.details === undefined) {
          return <NotFound />;
        }
        if (error) {
          if (isNotFound(error)) {
            return <NotFound />;
          }
          return <DataError onReload={reload} />;
        }

        const riskName = riskDetails.getName(riskDetails.details);

        return (
          <Record
            navBarHeight={navBarHeight}
            Header={
              <EntityDetailsHeader
                store={riskDetails}
                title={riskName}
                // The default first crumb is the entity's plural, so it would read "Risks" while
                // every other surface in the module reads "Risk Register".
                breadcrumbs={[
                  { label: 'Risk Register', link: riskDetails.path },
                  { label: 'Details', link: riskDetails.detailsPath(riskDetails.details.uuid) },
                ]}
                columns={
                  moduleEnabled
                    ? HEADER_FIELDS.map(([name, fieldKey, testId]) => ({
                        name,
                        testId: `risk-header-${testId}`,
                        value: <RiskHeaderValue store={riskDetails} fieldKey={fieldKey} />,
                      }))
                    : undefined
                }
                wrapColumns={moduleEnabled}
              />
            }
            Content={(onError) => <EntityDetailsContent onError={onError} store={riskDetails} />}
            activitiesWidth={moduleEnabled ? ACTIVITIES_WIDTH : undefined}
            Activities={{
              Content: (
                <EntityDetailsActivities
                  activities={riskDetails.activities}
                  activityTypeToComponent={activityTypeToComponent}
                />
              ),
              Footer: (
                <EntityDetailsActivitiesFooter activities={riskDetails.activities} placeholder="Add a comment…" />
              ),
            }}
          />
        );
      }}
    </EntitiesDetailsLoader>
  );
};

export default observer(RisksDetails);
